import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Users, 
  Plus,
  Edit,
  Trash2,
  Linkedin,
  Mail,
  Phone,
  Save,
  Loader2,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const AdminTeamPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewMember, setShowNewMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyMember = {
    first_name: '',
    last_name: '',
    role: '',
    bio: '',
    avatar_url: '',
    email: '',
    phone: '',
    linkedin: '',
    is_featured: false
  };
  
  const [newMember, setNewMember] = useState(emptyMember);

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      setLoading(true);
      const res = await api.get('/website/team');
      setTeam(res.data || []);
    } catch (error) {
      console.error('Error loading team:', error);
      toast.error('Erreur lors du chargement de l\'équipe');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMember = async () => {
    if (!newMember.first_name || !newMember.last_name) {
      toast.error('Prénom et nom requis');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post('/website/team', newMember);
      setTeam([...team, res.data]);
      setShowNewMember(false);
      setNewMember(emptyMember);
      toast.success('Membre ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;
    
    setSaving(true);
    try {
      await api.put(`/website/team/${editingMember.id}`, editingMember);
      setTeam(team.map(m => m.id === editingMember.id ? editingMember : m));
      setEditingMember(null);
      toast.success('Membre mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Supprimer ce membre ?')) return;
    
    try {
      await api.delete(`/website/team/${memberId}`);
      setTeam(team.filter(m => m.id !== memberId));
      toast.success('Membre supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleFeatured = async (member) => {
    try {
      await api.put(`/website/team/${member.id}`, { is_featured: !member.is_featured });
      setTeam(team.map(m => m.id === member.id ? {...m, is_featured: !m.is_featured} : m));
      toast.success(member.is_featured ? 'Retiré de la mise en avant' : 'Mis en avant');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  const MemberForm = ({ member, onChange }) => (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Prénom *</Label>
          <Input 
            value={member.first_name}
            onChange={(e) => onChange({...member, first_name: e.target.value})}
            placeholder="Jean"
          />
        </div>
        <div className="space-y-2">
          <Label>Nom *</Label>
          <Input 
            value={member.last_name}
            onChange={(e) => onChange({...member, last_name: e.target.value})}
            placeholder="Dupont"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Poste / Fonction</Label>
        <Input 
          value={member.role}
          onChange={(e) => onChange({...member, role: e.target.value})}
          placeholder="Directeur Artistique"
        />
      </div>
      <div className="space-y-2">
        <Label>Biographie</Label>
        <textarea 
          className="w-full p-2 border rounded-lg text-sm"
          rows={3}
          value={member.bio || ''}
          onChange={(e) => onChange({...member, bio: e.target.value})}
          placeholder="Courte description..."
        />
      </div>
      <div className="space-y-2">
        <Label>Photo (URL)</Label>
        <Input 
          value={member.avatar_url || ''}
          onChange={(e) => onChange({...member, avatar_url: e.target.value})}
          placeholder="https://..."
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Email</Label>
          <Input 
            type="email"
            value={member.email || ''}
            onChange={(e) => onChange({...member, email: e.target.value})}
            placeholder="jean@msc.fr"
          />
        </div>
        <div className="space-y-2">
          <Label>Téléphone</Label>
          <Input 
            value={member.phone || ''}
            onChange={(e) => onChange({...member, phone: e.target.value})}
            placeholder="06 12 34 56 78"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>LinkedIn</Label>
        <Input 
          value={member.linkedin || ''}
          onChange={(e) => onChange({...member, linkedin: e.target.value})}
          placeholder="https://linkedin.com/in/..."
        />
      </div>
      <div className="flex items-center gap-2 pt-2">
        <Switch 
          checked={member.is_featured || false}
          onCheckedChange={(checked) => onChange({...member, is_featured: checked})}
        />
        <Label className="cursor-pointer">Mettre en avant sur le site</Label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a365d]">Équipe</h1>
          <p className="text-[#64748b]">Gérez les membres de votre équipe affichés sur le site</p>
        </div>
        <Button onClick={() => setShowNewMember(true)} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un membre
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {team.filter(m => m.is_active !== false).map((member) => (
          <Card key={member.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center text-xl font-bold text-amber-600 overflow-hidden flex-shrink-0">
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt={member.first_name} className="w-full h-full object-cover" />
                  ) : (
                    `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#1a365d] truncate">
                      {member.first_name} {member.last_name}
                    </h3>
                    {member.is_featured && (
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-[#64748b]">{member.role}</p>
                  <div className="flex items-center gap-2 mt-3">
                    {member.email && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                        <a href={`mailto:${member.email}`}><Mail className="w-3 h-3" /></a>
                      </Button>
                    )}
                    {member.phone && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                        <a href={`tel:${member.phone}`}><Phone className="w-3 h-3" /></a>
                      </Button>
                    )}
                    {member.linkedin && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                          <Linkedin className="w-3 h-3" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setEditingMember(member)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDeleteMember(member.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {team.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun membre dans l'équipe</p>
            <Button onClick={() => setShowNewMember(true)} variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un membre
            </Button>
          </CardContent>
        </Card>
      )}

      {/* New Member Dialog */}
      <Dialog open={showNewMember} onOpenChange={setShowNewMember}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau membre</DialogTitle>
          </DialogHeader>
          <MemberForm member={newMember} onChange={setNewMember} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMember(false)}>Annuler</Button>
            <Button onClick={handleCreateMember} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier le membre</DialogTitle>
          </DialogHeader>
          {editingMember && (
            <MemberForm member={editingMember} onChange={setEditingMember} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMember(null)}>Annuler</Button>
            <Button onClick={handleUpdateMember} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTeamPage;
